import ProductGrid from '@nespresso/catalog-components-react/containers/ProductGrid';
import { useRef } from 'preact/hooks';
import type { Product } from '@/domain/Product.ts';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand.tsx';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import CoffeeProductCard from '../../cards/CoffeeProductCard/CoffeeProductCard.tsx';
import styles from './ViewAlternativesDrawer.module.scss';
import { FAVORITE_LIST, trackAlternativeImpression } from '../../Tracking.ts';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import AccessoryProductCard from '../../cards/AccessoryProductCard/AccessoryProductCard.tsx';
import {
  alternativesItemListName,
  type Translations
} from '../../config/index.ts';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import { RAISED_BY } from '@/widgets/services/TrackingService.ts';

export type ViewAlternativesDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
};

export default function ViewAlternativesDrawer({
  isOpen,
  onClose,
  product
}: ViewAlternativesDrawerProps) {
  const { t } = useTranslations<Translations>();
  const { inFavorites } = useRoute();
  const {
    financialSettings: { defaultCurrency },
    discoveryOfferBundleProducts
  } = useMarketConfig();

  const impressionSent = useRef(false);

  const handlePurchaseSectionImpression = (idx: number) => {
    // only send impression once, for first alt-product in list
    if (idx > 0 || impressionSent.current || !product.alternativeProducts) {
      return;
    }

    const isDiscoveryOffer = product.alternativeProducts.some((altProduct) =>
      discoveryOfferBundleProducts.includes(altProduct.id)
    );

    trackAlternativeImpression(
      product.alternativeProducts,
      product,
      defaultCurrency,
      isDiscoveryOffer,
      1,
      inFavorites ? FAVORITE_LIST : RAISED_BY
    );

    impressionSent.current = true;
  };

  return (
    <ModalOnDemand
      isOpen={isOpen}
      onClose={onClose}
      position="side"
      heading={t('productList.viewAlternatives.title')}
      closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
    >
      <p>{t('productList.viewAlternatives.description')}</p>
      <ProductGrid forceLandscape className={styles.grid}>
        {product.alternativeProducts?.map((altProduct, idx) => {
          if (altProduct.type === 'accessory') {
            return (
              <AccessoryProductCard
                key={altProduct.id}
                product={altProduct}
                onPurchaseSectionImpression={() =>
                  handlePurchaseSectionImpression(idx)
                }
                class={styles.card}
                itemListName={alternativesItemListName}
              />
            );
          }

          return (
            <CoffeeProductCard
              key={altProduct.id}
              product={altProduct}
              onPurchaseSectionImpression={() =>
                handlePurchaseSectionImpression(idx)
              }
              class={styles.card}
              itemListName="Alternatives"
              showFavIconWithTitle
            />
          );
        })}
      </ProductGrid>
    </ModalOnDemand>
  );
}
